import { create } from 'zustand';
import { healthService } from 'src/services/health';
import { DrugInfo, HealthIssueDetail, HealthCondition, MedicationTemplate } from 'src/services/interfaces/health';
import { Issue} from 'src/services/interfaces/application';

interface HealthState {
  issueDetails: HealthIssueDetail[];
  conditions: HealthCondition[];
  drugInfo: DrugInfo[];
  medicationTemplates: MedicationTemplate[];
  isLoading: boolean;
  error: string | null;
  isFailValidation: boolean;
  issues: Issue[];
  getIssues: () => Promise<void>;
  getIssueDetails: () => Promise<void>;
  createIssueDetail: (data: HealthIssueDetail) => Promise<void>;
  getIssueDetail: (id: number) => Promise<void>;
  updateIssueDetail: (id: number, data: HealthIssueDetail) => Promise<void>;
  deleteIssueDetail: (id: number) => Promise<void>;
  getConditions: () => Promise<void>;
  createCondition: (data: HealthCondition) => Promise<void>;
  getCondition: (id: number) => Promise<void>;
  updateCondition: (id: number, data: HealthCondition) => Promise<void>;
  deleteCondition: (id: number) => Promise<void>;
  getDrugInfoAutocomplete: (query: string, signal?: AbortSignal) => Promise<MedicationTemplate[]>;
  setMedicationTemplates: (templates: MedicationTemplate[]) => void;
  setError: (error: string | null) => void;
  setFailValidation: (isFailValidation: boolean) => void;
}

export const useHealthStore = create<HealthState>((set) => ({
  issueDetails: [],
  conditions: [],
  drugInfo: [],
  medicationTemplates: [],
  isLoading: false,
  error: null,
  isFailValidation: false,
  issues: [],

  getIssues: async () => {
    set({ isLoading: true, error: null });
    try {
      const issues = await healthService.getIssues();
      set({ issues, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch issues.', isLoading: false });
    }
  },

  getIssueDetails: async () => {
    set({ isLoading: true, error: null });
    try {
      const issueDetails = await healthService.getIssueDetails();
      set({ issueDetails, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch issue details.', isLoading: false });
    }
  },

  createIssueDetail: async (data: HealthIssueDetail) => {
    set({ isLoading: true, error: null });
    try {
      const issueDetail = await healthService.createIssueDetail(data);
      set({ issueDetails: [...useHealthStore.getState().issueDetails, issueDetail], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to create issue detail.', isLoading: false });
    }
  },

  getIssueDetail: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const issueDetail = await healthService.getIssueDetail(id);
      set({ issueDetails: [issueDetail], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch issue detail.', isLoading: false });
    }
  },

  updateIssueDetail: async (id: number, data: HealthIssueDetail) => {
    set({ isLoading: true, error: null });
    try {
      const issueDetail = await healthService.updateIssueDetail(id, data);
      set({
        issueDetails: useHealthStore.getState().issueDetails.map((item) =>
          item.issueId === id ? issueDetail : item
        ),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to update issue detail.', isLoading: false });
    }
  },

  deleteIssueDetail: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await healthService.deleteIssueDetail(id);
      set({
        issueDetails: useHealthStore.getState().issueDetails.filter((item) => item.issueId !== id),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to delete issue detail.', isLoading: false });
    }
  },

  getConditions: async () => {
    set({ isLoading: true, error: null });
    try {
      const conditions = await healthService.getConditions();
      set({ conditions, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch conditions.', isLoading: false });
    }
  },

  createCondition: async (data: HealthCondition) => {
    set({ isLoading: true, error: null });
    try {
      const condition = await healthService.createCondition(data);
      set({ conditions: [...useHealthStore.getState().conditions, condition], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to create condition.', isLoading: false });
    }
  },

  getCondition: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const condition = await healthService.getCondition(id);
      set({ conditions: [condition], isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch condition.', isLoading: false });
    }
  },

  updateCondition: async (id: number, data: HealthCondition) => {
    set({ isLoading: true, error: null });
    try {
      const condition = await healthService.updateCondition(id, data);
      set({
        conditions: useHealthStore.getState().conditions.map((item) =>
          item.key === condition.key ? condition : item
        ),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to update condition.', isLoading: false });
    }
  },

  deleteCondition: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await healthService.deleteCondition(id);
      set({
        conditions: useHealthStore.getState().conditions.filter((item) => item.key !== id.toString()),
        isLoading: false,
      });
    } catch (error) {
      set({ error: 'Failed to delete condition.', isLoading: false });
    }
  },

  getDrugInfoAutocomplete: async (query: string, signal?: AbortSignal) => {
    set({ isLoading: true, error: null });
    try {
      const drugInfo = await healthService.getDrugInfoAutocomplete(query);
      // DrugInfo to MedicationTemplate
			// TODO replace it future
      const templates = drugInfo.map((drug) => ({
        rxcui: drug.rxcui,
        name: drug.name,
        fullName: drug.fullName,
        form: drug.rxtermsDoseForm || drug.rxnormDoseForm || 'Unknown',
        dosages: drug.strength ? [drug.strength] : ['Unknown'],
        frequencies: ['Once a Day', 'Twice a Day', 'Every 8 hours'],
        applicants: ['Me', 'My Spouse', 'My Child'],
      }));
      set({ drugInfo, medicationTemplates: templates, isLoading: false });
      return templates; // Return templates instead of drugInfo
    } catch (error) {
      set({ error: 'Failed to fetch drug info.', isLoading: false });
      return []; // Return empty array on error
    }
  },

  setMedicationTemplates: (templates: MedicationTemplate[]) => {
    set({ medicationTemplates: templates });
  },

  setError: (error: null | string) => {
    set({ error });
  },

  setFailValidation: (isFailValidation: boolean) => {
    set({ isFailValidation });
  },
}));
